import React, { useEffect, useId } from 'react';
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  ArrowDownIcon,
  ArrowUpIcon,
} from 'shared/ui/icons';
import useWindowDimensions from 'shared/hooks/useWindowDimensions';
import Spin from 'antd/lib/spin';
import { getTranslation } from 'shared/utils/translate/Translate';
import { Checkbox } from 'shared/ui/checkbox';

const Table = ({
  limit,
  page,
  total,
  data = [],
  columns = [],
  pageSize = 5,
  pagination = true,
  className = '',
  handlePageSizeChange = () => {},
  handlePaginationChange = () => {},
  handleSortOrderChange = () => {},
  handleSelectAllCheckBox = () => {},
  rowClassName = () => '',
  onRowClick = null,
  loading = false,
  customEmpty = null,
  empty = null,
}) => {
  const { isMobile } = useWindowDimensions();
  // Per-instance id: a page can hold two paginated tables (e.g. company list +
  // unwritten rules), and a shared literal id would pair each label with the
  // first table's select.
  const rowsPerPageId = useId();

  const [sortOrder, setSortOrder] = React.useState('');
  const [checkAll, setCheckAll] = React.useState(false);
  const totalPages = Math.ceil(total / limit);

  useEffect(() => {
    setCheckAll(false);
  }, [limit, pageSize, page]);

  const handlePrevious = () => {
    if (page > 1) {
      handlePaginationChange(page - 1, pageSize);
    }
  };

  const handleNext = () => {
    if (page < totalPages) {
      handlePaginationChange(page + 1, pageSize);
    }
  };

  const handleSortOrder = (key, order) => {
    setSortOrder(`${order}${key}`);
    handleSortOrderChange(`${order}${key}`);
  };

  const getText = (text) => getTranslation('All', text);

  return (
    <div className={`relative pb-5 ${className}`}>
      {loading && data.length > 0 && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/60 rounded-xl">
          <Spin />
        </div>
      )}
      <div className="w-full overflow-auto">
        <table className="min-w-full border-collapse">
          <thead className="bg-gray-100 text-gray-500">
            <tr>
              {columns?.map(
                (col) =>
                  col && (
                    <th
                      key={`${col?.key}-${page}-${pageSize}-${checkAll}`}
                      className={`px-4 py-2 text-sm font-normal ${col?.align} ${col?.classHead ?? ''}`}
                    >
                      {col?.key === 'checkbox' ? (
                        <Checkbox
                          key={`checkbox-all-${page}-${pageSize}-${checkAll}`}
                          classNameLabel="relative -top-[6px]"
                          onClick={(e) => {
                            e.stopPropagation();
                            let checkedValue = e.target.checked;
                            setCheckAll(checkedValue);
                            handleSelectAllCheckBox(checkedValue);
                          }}
                          checked={checkAll}
                        />
                      ) : (
                        <div className="flex items-center">
                          {col?.label}
                          {col?.isSortable && (
                            <div className="flex flex-col ml-2 -space-y-1">
                              <ArrowUpIcon
                                width="12"
                                height="12"
                                color={
                                  sortOrder == `${col?.key}` ? 'black' : ''
                                }
                                onClick={() => handleSortOrder(col?.key, '')}
                              />
                              <ArrowDownIcon
                                height="12"
                                width="12"
                                color={
                                  sortOrder == `-${col.key}` ? 'black' : ''
                                }
                                onClick={() => handleSortOrder(col?.key, '-')}
                              />
                            </div>
                          )}
                        </div>
                      )}
                    </th>
                  )
              )}
            </tr>
          </thead>
          <tbody data-testid="table-body">
            {empty !== null ? (
              <tr>
                <td
                  colSpan={columns.length}
                  className="h-40 px-4 py-4 text-center text-gray-500"
                >
                  {empty}
                </td>
              </tr>
            ) : Array.isArray(data) && data.length > 0 ? (
              data.map((row, index) => (
                <tr
                  key={index}
                  className={`text-gray-700 ${
                    index % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                  } border-b border-gray-200 ${onRowClick && 'cursor-pointer'} ${rowClassName(row)}`}
                  onClick={() => onRowClick && onRowClick(row)}
                >
                  {columns.map(
                    (col) =>
                      col && (
                        <td
                          key={col.key}
                          className={`px-4 py-4 text-sm font-normal ${col.classCell ?? ''}`}
                        >
                          {React.isValidElement(row[col.key])
                            ? row[col.key]
                            : row[col.key] || '-'}
                        </td>
                      )
                  )}
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan={columns.length}
                  className="h-40 px-4 py-4 text-center text-gray-500"
                >
                  {window.navigator.onLine ? (
                    loading ? (
                      <Spin />
                    ) : customEmpty ? (
                      customEmpty
                    ) : (
                      getText('Data Tidak Tersedia')
                    )
                  ) : (
                    getText('Kesalahan Jaringan, Internet Terputus')
                  )}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {data.length > 0 && pagination && !empty && (
        <div
          className={`mt-4 flex ${isMobile ? 'flex-col items-end' : 'items-center justify-end'} right-0 bottom-0 text-sm text-gray-600 gap-4`}
        >
          <div>
            <div className="flex items-center space-x-2">
              <label htmlFor={rowsPerPageId} className="text-gray-700">
                {getText('Baris per tampilan')}
              </label>
              <select
                id={rowsPerPageId}
                value={pageSize}
                onChange={(e) => {
                  handlePageSizeChange(e.target.value);
                }}
                className="rounded-md border px-2 py-1 text-gray-700 focus:ring-blue-500 focus:outline-none"
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div>
              {isNaN(pageSize) || isNaN(total)
                ? 0
                : pageSize * page >= total
                  ? total
                  : pageSize * page}{' '}
              {getText('dari')} {isNaN(total) ? 0 : total} {getText('data')}
            </div>
            <div className="flex gap-3">
              <button
                aria-label={'Previous Page'}
                className={`p-2  ${page === 1 ? 'bg-gray-100' : 'bg-gray-200 hover:bg-gray-200'} rounded-md`}
                onClick={handlePrevious}
                disabled={page === 1}
              >
                <ArrowLeftIcon />
              </button>
              <button
                onClick={handleNext}
                aria-label={'Next Page'}
                disabled={page === totalPages}
                className={`p-2 bg-gray-100 ${page === totalPages ? 'bg-gray-100' : 'bg-gray-200 hover:bg-gray-200'} rounded-md`}
              >
                <ArrowRightIcon />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export { Table };
